import moment from 'moment';
import Chart from 'react-apexcharts';

import { useStoreSales } from '@/api/stores';
import QueryResult from '@/components/QueryResult';
import { formatCentsToEuro } from '@/helpers/money';

type DatePickerProps = {
  startDate: number;
  endDate: number;
};

function SalesThisWeek({
  startDate,
  endDate,
}: DatePickerProps) {
  const storeSales = useStoreSales(1, startDate, endDate);
  const borderColor = '#F3F4F6';
  const labelColor = '#6B7280';
  const opacityFrom = 0.45;
  const opacityTo = 0;

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const options: ApexCharts.ApexOptions = {
    stroke: {
      curve: 'smooth',
    },
    chart: {
      type: 'area',
      fontFamily: 'Inter, sans-serif',
      foreColor: labelColor,
      toolbar: {
        show: false,
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        opacityFrom,
        opacityTo,
        type: 'vertical',
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      style: {
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
      },
    },
    grid: {
      show: true,
      borderColor,
      strokeDashArray: 1,
      padding: {
        left: 35,
        bottom: 15,
      },
    },
    markers: {
      size: 5,
      strokeColors: '#ffffff',
      hover: {
        size: undefined,
        sizeOffset: 3,
      },
    },
    xaxis: {
      categories: [
        ...(storeSales.data?.results.map(
          (sale: Api.SalesReport) =>
            new Date(
              moment(sale.date).utc().format(),
            ).toLocaleDateString('es-ES', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            }),
        ) ?? []),
      ],
      labels: {
        style: {
          colors: [labelColor],
          fontSize: '14px',
          fontWeight: 500,
        },
      },
      axisBorder: {
        color: borderColor,
      },
      axisTicks: {
        color: borderColor,
      },
      crosshairs: {
        show: true,
        position: 'back',
        stroke: {
          color: borderColor,
          width: 1,
          dashArray: 10,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: [labelColor],
          fontSize: '14px',
          fontWeight: 500,
        },
        formatter(value) {
          return `$${value}`;
        },
      },
    },
    legend: {
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: [labelColor],
      },
      itemMargin: {
        horizontal: 10,
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          xaxis: {
            labels: {
              show: false,
            },
          },
        },
      },
    ],
  };
  const series = [
    {
      name: 'Santander',
      data:
        storeSales.data?.results.map(
          (sale: Api.SalesReport) =>
            formatCentsToEuro(sale.total) ?? 0,
        ) ?? [],
      color: '#FF7201',
    },
  ];

  return (
    <QueryResult query={storeSales}>
      <Chart
        height={420}
        options={options}
        series={series}
        type="area"
      />
    </QueryResult>
  );
}

export default SalesThisWeek;
